<template>
  <div class="user-account-menu">
    <div class="user-account-menu__identity">
      <AccountAvatar class="user-account-menu__avatar" />
      <span class="user-account-menu__data">
        <strong class="user-account-menu__name">{{ user.name }}</strong>
        <span class="user-account-menu__email">{{ user.email }}</span>
      </span>
    </div>

    <div
      v-if="canSwitchPromoter"
      class="user-account-menu__session"
      role="status"
    >
      <span class="user-account-menu__label">Você está acessando</span>
      <span class="user-account-menu__promoter">
        <strong class="user-account-menu__promoter-name">{{ promoterName || 'Sua promotora' }}</strong>
        <span
          class="tag"
          :class="session.tone"
        >{{ session.label }}</span>
      </span>
      <span class="user-account-menu__help">{{ session.help }}</span>
    </div>

    <ul class="user-account-menu__actions">
      <li
        v-for="action in actions"
        :key="action.id"
      >
        <router-link
          v-if="action.route"
          :to="destination(action.route)"
          class="user-account-menu__action"
          @click.native="$emit('close')"
        >
          {{ action.label }}
        </router-link>
        <button
          v-else
          type="button"
          class="user-account-menu__action"
          :class="{ 'is-accounts': action.id === 'accounts' }"
          @click="run(action.id)"
        >
          {{ action.label }}
          <small v-if="action.id === 'accounts' && accesses.length">{{ accesses.length }} contas</small>
        </button>
      </li>
    </ul>

    <p class="user-account-menu__version">
      Versão {{ version }}
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { handler as handlerError } from '@/utils/message';

import config from '@/data/config';

import { accountsAPI } from '@/services';

import { useAppVersion } from '@/composables/useAppVersion';
import {
  accountActions, allowsPromoterSwitch, appVersion, sessionState,
} from '@/utils/userAccount';
import loadingMixin from '@/mixins/loadingView.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import AccountAvatar from '@/components/AccountAvatar.vue';

export default {
  name: 'UserAccountMenu',
  components: {
    AccountAvatar,
  },
  mixins: [
    loadingMixin,
    roleManagerMixin,
  ],
  computed: {
    ...mapState({
      user: (state) => state.user.userInfo,
      accesses: (state) => state.user.promoterAccesses,
    }),
    ...mapGetters({
      isInternalUser: 'user/isInternalUser',
      isPromoterActive: 'user/isPromoterActive',
      isPromoterBlocked: 'user/isPromoterBlocked',
      promoterName: 'user/promoterName',
    }),
    canSwitchPromoter() {
      return allowsPromoterSwitch({
        user: this.user,
        isInternalUser: this.isInternalUser,
        isPromoterActive: this.isPromoterActive,
      });
    },
    session() {
      return sessionState(this.isPromoterActive, this.isPromoterBlocked);
    },
    actions() {
      return accountActions({
        canSwitchPromoter: this.canSwitchPromoter,
        isInternalUser: this.isInternalUser,
        isCollector: this.isCollector,
        rewardsDisabled: Boolean(this.user?.rewards_disabled),
      });
    },
    version() {
      return appVersion(config?.DEPLOY_VERSION);
    },
  },
  mounted() {
    if (this.canSwitchPromoter) this.loadAccesses().catch(() => {});
  },
  methods: {
    ...mapActions({
      setToken: 'app/SET_TOKEN',
      loadAccesses: 'user/LOAD_PROMOTER_ACCESSES',
    }),
    destination(name) {
      return name === 'edit-user' ? { name, params: { id: this.user.id } } : { name };
    },
    run(id) {
      this.$emit('close', id !== 'accounts');

      if (id === 'accounts') this.$emit('accounts');
      else if (id === 'reload') this.reloadSystem();
      else if (id === 'logout') this.logout();
    },
    reloadSystem() {
      const { clearVersionGuard } = useAppVersion();
      clearVersionGuard();
      window.location.reload();
    },
    async logout() {
      this.loading = true;

      await accountsAPI
        .logoutUser()
        .then((response) => {
          if (response.data.data.message) {
            this.setToken(false);
            this.$router.push({ name: 'login' });
          }
        })
        .catch((err) => {
          handlerError(err);
          this.$router.push({ name: 'login' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-account-menu__identity {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid map.get($stone-types, "100");

  align-items: center;
}

.user-account-menu__avatar {
  --avatar-size: 44px;
  --avatar-icon: 1rem;
}

.user-account-menu__data {
  display: flex;
  min-width: 0;

  flex-direction: column;
}

.user-account-menu__name {
  color: map.get($stone-types, "900");
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.33;
}

.user-account-menu__email {
  overflow: hidden;

  color: map.get($stone-types, "500");
  font-size: 0.8125rem;
  line-height: 1.38;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-account-menu__session {
  display: flex;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  background-color: map.get($stone-types, "50");
  border-bottom: 1px solid map.get($stone-types, "100");

  flex-direction: column;
}

.user-account-menu__label {
  color: map.get($stone-types, "500");
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.user-account-menu__promoter {
  display: flex;
  gap: 0.625rem;

  flex-wrap: wrap;
  align-items: center;
}

.user-account-menu__promoter-name {
  color: map.get($stone-types, "900");
  font-size: 0.9375rem;
  font-weight: 600;
}

.user-account-menu__help {
  color: map.get($stone-types, "500");
  font-size: 0.8125rem;
  line-height: 1.38;
}

.user-account-menu__actions {
  padding: 0.5rem 0;
}

.user-account-menu__action {
  display: inline-flex;
  width: 100%;
  min-height: $target-touch;
  padding: 0.75rem 1rem;
  border: 0;
  border-radius: 0;

  background: none;
  color: map.get($stone-types, "700");
  text-decoration: none;
  font-size: 0.9375rem;
  text-align: left;

  cursor: pointer;
  align-items: center;

  &:hover {
    background-color: map.get($stone-types, "50");
    color: map.get($stone-types, "900");
  }

  @include focus-ring(-2px);

  &.is-accounts {
    gap: 0.5rem;

    justify-content: space-between;
    color: $prata-green-700;
    font-weight: 600;

    &:hover {
      color: $prata-green-800;
    }

    small {
      color: map.get($stone-types, "400");
      font-size: 0.8125rem;
      font-weight: 400;
    }
  }
}

.user-account-menu__version {
  padding: 0.75rem 1rem;
  border-top: 1px solid map.get($stone-types, "100");

  color: map.get($stone-types, "400");
  font-size: 0.6875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
